import { isChrome } from '@confluence/browser-helper';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

// Called in development only. Overrides console logging methods in Chrome and trims down the excessively long
// component/stack traces in React errors to reduce the memory consumption in Chrome devtools.
export function devTrimConsoleTraces(): void {
	const maxStackSize = StatsigConfigurations.getNumber(
		'confluence_frontend_dev_console_max_stack_size',
		0,
	);

	// Default value means don't trim stacks.
	if (maxStackSize === 0 || !isChrome()) {
		return;
	}

	// The code below takes many hints from React devtools code
	// https://github.com/facebook/react/blob/c80a07509582daadf275f36ffe7a88c3b12e9db4/packages/react-devtools-shared/src/hook.js#L456
	for (const method of ['error', 'warn']) {
		const originalMethod = console[method];

		console[method] = function (...args) {
			if (args.length === 0) {
				return;
			}

			const lastArg = args[args.length - 1];

			// React's custom built component stack strings match "\s{4}in"
			// Chrome's prefix matches "\s{4}at"
			if (typeof lastArg === 'string' && /\s{4}(in|at)\s{1}/.test(lastArg)) {
				const firstArg = args[0];
				const newArgs = args.slice();

				if (args.length > 1 && typeof firstArg === 'string' && firstArg.endsWith('%s')) {
					newArgs[0] = firstArg.slice(0, firstArg.length - 2); // Strip the %s param
				}

				// Create a fake Error so that when we print it we get native source maps. Every
				// browser will print the .stack property of the error and then parse it back for source
				// mapping.
				const fakeError = new Error('');
				// It has to start with ^[\w.]*Error\b to trigger stack formatting.
				fakeError.name = 'Error Component Stack';
				// Overwrite the stack property with the trimmed stack trace.
				fakeError.stack = lastArg
					.split('\n')
					.slice(0, maxStackSize + 1)
					.join('\n');

				newArgs[newArgs.length - 1] = fakeError;

				// If the developer has React devtools extension installed, it contains its own override logic for console methods,
				// which is downstream of our implementation here. If the extension's "append component stacks" setting is on,
				// then it will end up appending a second stack trace to the one we're providing. Thus we need to force that
				// setting to be off temporarily.
				let originalAppendComponentStack;
				const reactDevtoolsSettings = window.__REACT_DEVTOOLS_GLOBAL_HOOK__?.settings;
				if (reactDevtoolsSettings) {
					originalAppendComponentStack = reactDevtoolsSettings.appendComponentStack;
					reactDevtoolsSettings.appendComponentStack = false;
				}

				originalMethod(...newArgs);

				if (reactDevtoolsSettings) {
					reactDevtoolsSettings.appendComponentStack = originalAppendComponentStack;
				}
			} else {
				originalMethod(...args);
			}
		};
	}
}
